import { formatDateDMY } from "../lib/dates";

const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const JS_TO_MON = [6, 0, 1, 2, 3, 4, 5];

function pad(n) {
  return String(n).padStart(2, "0");
}

export function addOneHour(hhmm) {
  const [h, m] = String(hhmm || "00:00").split(":").map(Number);
  const mins = (h * 60 + (m || 0) + 60) % (24 * 60);
  return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
}

export function formatHourRange(hour) {
  return `${hour} - ${addOneHour(hour)}`;
}

function mondayOf(iso) {
  const d = new Date(`${iso}T00:00:00`);
  const diff = JS_TO_MON[d.getDay()];
  d.setDate(d.getDate() - diff);
  return d;
}

function isoFromDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function WeeklyAvailabilityPanel({
  selected,
  onSelectDay,
  slotHours = [],
  hours = [],
  bookedHours = [],
  dayCounts = {},
  onToggleHour,
  onSave,
  onApplyWeek,
  onApplyMonth,
  saving,
  error,
  bookable = true,
}) {
  const week = [];
  const start = mondayOf(selected || isoFromDate(new Date()));
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = isoFromDate(d);
    week.push({ iso, label: DAY_ORDER[i], count: dayCounts[iso] || 0 });
  }
  const booked = new Set(bookedHours);

  return (
    <div className="space-y-5" data-testid="weekly-availability">
      <div>
        <p className="text-sm font-semibold text-[#311b52] mb-2">Weekly availability</p>
        <div className="flex flex-wrap gap-2">
          {week.map((d) => {
            const on = d.iso === selected;
            return (
              <button
                key={d.iso}
                type="button"
                onClick={() => onSelectDay(d.iso)}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold border ${
                  on ? "bg-[#f37721] text-white border-[#f37721]" : "bg-white text-[#311b52] border-[#e5e7eb] hover:border-[#f37721]/50"
                }`}
                data-testid={`avail-weekday-${d.label.toLowerCase()}`}
              >
                {d.label}{d.count ? ` (${d.count})` : ""}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-[#6b7280] mt-2">{formatDateDMY(selected)}</p>
      </div>

      {!bookable ? (
        <p className="text-sm text-[#6b7280]">This day is not open for bookings.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {slotHours.map((s) => {
            const hour = s.hour || s;
            const on = hours.includes(hour);
            const isBooked = booked.has(hour);
            return (
              <button
                key={hour}
                type="button"
                disabled={saving || isBooked}
                onClick={() => onToggleHour(hour)}
                className={`rounded-xl border px-3 py-3 text-xs font-semibold text-left transition ${
                  isBooked ? "bg-orange-50 border-orange-200 text-orange-800"
                    : on ? "bg-[#fff4ec] border-[#f37721] text-[#311b52]"
                    : "bg-white border-[#eee] text-[#6b7280] hover:border-[#f37721]/40"
                } ${saving ? "opacity-60" : ""}`}
                data-testid={`avail-hour-${hour}`}
              >
                {s.label && s.label.includes("-") ? s.label : formatHourRange(hour)}
                {isBooked ? <span className="block text-[10px] mt-1">Booked</span> : null}
              </button>
            );
          })}
        </div>
      )}

      {error && <p className="text-sm text-[#ee4036]">{error}</p>}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" disabled={saving} onClick={onApplyWeek} className="text-xs font-semibold px-3 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] disabled:opacity-40">
          {saving ? "Saving…" : "Apply to this week"}
        </button>
        <button type="button" disabled={saving} onClick={onApplyMonth} className="text-xs font-semibold px-3 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] disabled:opacity-40">
          Apply to this month
        </button>
        <button type="button" disabled={saving || !bookable} onClick={onSave} className="text-sm font-semibold px-5 py-2 rounded-full bg-[#f37721] text-white disabled:opacity-40" data-testid="avail-save">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
